import ShortUniqueId from "short-unique-id";
import { v4 as uuidv4 } from "uuid"; // For standard UUID generation
import { ENV_VAR } from "./Constant";
import { getEnvFrom } from "store/AppGlobalStore";

export const getEnvVariableValue = (key: string): string | undefined => {
  if (!key) throw new Error("Please pass the key");

  // Host-provided env via AppGlobalStore
  const envFrom = getEnvFrom();
  if (envFrom && (window as any)[envFrom]?.[key] !== undefined) {
    return (window as any)[envFrom][key];
  }

  // Standalone window.env
  if ((window as any).env?.[key] !== undefined) {
    return (window as any).env[key];
  }

  // Vite fallback
  if (import.meta.env[key] !== undefined) {
    return import.meta.env[key];
  }

  return undefined;
};

/**
 * Wait for environment variables to be loaded
 * @returns Promise that resolves when window.env is available
 */
export const waitForEnvReady = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if (window?.env) {
      resolve(true);
    } else {
      const checkEnv = () => {
        if (window?.env) {
          resolve(true);
        } else {
          setTimeout(checkEnv, 10);
        }
      };
      checkEnv();
    }
  });
};

/**
 *
 * @returns oject which contains device information
 */
// export const getDeviceInformation = () => {
//   return {
//     isMobile: navigator.userAgentData.mobile,
//     platform: navigator.userAgentData.platform,
//   };
// };

/**
 *
 * @param {any} data
 * @returns encoded string
 */
export const encode = (data: string) => {
  return btoa(data);
};

/**
 *
 * @param {string} data // encrypted data
 * @returns decoded string
 */
export const decode = (data: string) => {
  return atob(data);
};

/**
 *
 * @param {function} onSuccessCallBack
 * @param {function} onErrorCallBack
 */
interface GeolocationError {
  code: number;
  message: string;
}

interface GeolocationPosition {
  coords: {
    latitude: number;
    longitude: number;
  };
}

type SuccessCallback = (position: GeolocationPosition) => void;
type ErrorCallback = (error: GeolocationError) => void;

export const getGeoLocation = (
  onSuccessCallBack: SuccessCallback,
  onErrorCallBack: ErrorCallback
): void => {
  if (!("geolocation" in navigator)) {
    const error: GeolocationError = {
      code: 0,
      message: "Geolocation not supported",
    };
    onErrorCallBack(error);
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (position: GeolocationPosition) => onSuccessCallBack(position),
    (error: GeolocationError) => onErrorCallBack(error)
  );
};

export const getLocalLanguage = () => {
  const localStorageObject = JSON.parse(getLocalStorage("defaultLocale"));
  return localStorageObject.defaultLanguage;
};

/**
 *
 * @param {string} data
 */
export const copyData = (data: string): void => {
  navigator.clipboard
    .writeText(data)
    .then(() => {
      console.log(">> Copied!!");
    })
    .catch(() => {
      console.log(">> Fail to copy");
    });
};

/**
 *
 * @param {string} key
 * read data from local storage
 */
export const getLocalStorage = (key: string) => {
  return localStorage.getItem(key) ?? "";
};
/**
 *
 * @param {string} key
 * @param {string | object} value
 */
export const setLocalStorage = (key: string, value: string) => {
  localStorage.setItem(key, value);
};

/**
 * Generates a UUID v4.
 *
 * @returns {string} A UUID v4 string in the format `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.
 * @example
 * const uuid = getRandomUUID();
 * console.log(uuid); // '1b9d6bcd-bbfd-4b2d-9b5d-ab8dfbbd4bed'
 */
export const getRandomUUID = (): string => {
  return uuidv4();
};

/**
 * Generates a short unique ID of 8 characters.
 *
 * @returns {string} A unique string of 8 characters.
 * @example
 * const shortId = getShortUUID();
 * console.log(shortId); // 'p0ZoB1Fw'
 */
export const getShortUUID = (): string => {
  const uid = new ShortUniqueId({ length: 8 });
  return uid();
};

/**
 *
 * @returns boolean value of the network status
 */
export const isOnline = () => {
  return navigator.onLine;
};

// multiple date formats
function addZero(i: number | string) {
  if (typeof i === "number" && i < 10) {
    i = "0" + i;
  }
  return i;
}

/**
 *
 * @param {integer} month
 * @returns previous date based on parameter passed or it default return on month back date
 */
export const getPrevMonthDate = (month = 1) => {
  const prevDate = new Date();
  prevDate.setMonth(prevDate.getMonth() - month);
  return prevDate;
};
/**
 *
 * @param {Date | string} date
 * @returns formatted Time With AM / PM
 */
export const formatTimeIn12HourFormat = (dateInString: string): string => {
  const date = new Date(dateInString);
  let hours: number = date.getHours();
  let minutes: number | string = date.getMinutes();
  const ampm: string = hours >= 12 ? "pm" : "am";

  // Convert hours to 12-hour format
  hours = hours % 12 || 12;

  // Add leading zero to minutes if needed
  minutes = minutes < 10 ? "0" + minutes : minutes;

  const formattedTime = `${hours}:${minutes} ${ampm}`;
  return formattedTime;
};

/**
 *
 * @param {string} dateToFormat
 * @returns  formatted string from date object
 */
export const formattedDate = (dateToFormat: string): string => {
  if (!dateToFormat) return "";

  const addZero = (num: number): string => (num < 10 ? "0" + num : String(num));

  const myDate = new Date(dateToFormat);
  const month: string = addZero(myDate.getMonth() + 1);
  const date: string = addZero(myDate.getDate());
  const year: number = myDate.getFullYear();
  const hh: string = addZero(myDate.getHours());
  const mm: string = addZero(myDate.getMinutes());
  const ss: string = addZero(myDate.getSeconds());

  return `${date}/${month}/${year} ${hh}:${mm}:${ss}`;
};

/** Same as formattedDate but without seconds: DD/MM/YYYY HH:mm. */
export const formattedDateHourMinute = (dateToFormat: string): string => {
  if (!dateToFormat) return "";
  return formattedDate(dateToFormat).slice(0, -3);
};

// ---- Standard Validator ----
/**
 * Standard Validator functions to be used with simple-react-validator package
 * @returns validation object
 */
export const getStandardValidator = () => {
  return {
    mandatory: {
      message: "validator.mandatory",
      rule: (val: any, params?: any, validator?: any): boolean => {
        if (val.toString()) {
          return true;
        } else {
          return false;
        }
      },
      required: true, // optional
    },
    onlyPositive: {
      message: "Negative values are not allowed",
      rule: (val: any, params?: any, validator?: any): boolean => {
        if (val) {
          const isPositive = Math.sign(Number(val));
          if (isPositive < 0) {
            return false;
          }
          return true;
        } else {
          return true;
        }
      },
    },
    onlyInteger: {
      message: "Decimals not allowed",
      rule: (val: any, params?: any, validator?: any): boolean => {
        if (val) {
          const numericValue = Number(val);
          if (!(numericValue || numericValue === 0) && !isNaN(numericValue)) {
            return false;
          }
          const splitValue = val.toString().split(".");
          if (splitValue) {
            if (splitValue.length > 1) {
              return false;
            }
            return true;
          } else {
            return false;
          }
        } else {
          return true;
        }
      },
      messageReplace: (message: string, params?: any) => {
        // return message.replace(":type", "AA");
      },
    },
    max: {
      message: ":type",
      rule: (val: any, params?: any, validator?: any): boolean => {
        if (params[1] === "num") {
          if (parseFloat(val) > parseFloat(params[0])) {
            return false;
          } else {
            return true;
          }
        } else {
          if (val.length > parseFloat(params[0])) {
            return false;
          } else {
            return true;
          }
        }
      },
      messageReplace: (message: string, params?: any) => {
        if (params[1] === "num") {
          return message.replace(":type", `Max value is ${params[0]}`);
        } else {
          return message.replace(":type", `Max length ${params[0]} chars`);
        }
      },
    },
    min: {
      message: ":type",
      rule: (val: any, params?: any, validator?: any): boolean => {
        if (params[1] === "num") {
          if (parseFloat(val) < parseFloat(params[0])) {
            return false;
          } else {
            return true;
          }
        } else {
          if (val.length < parseFloat(params[0])) {
            return false;
          } else {
            return true;
          }
        }
      },
      messageReplace: (message: string, params?: any) => {
        if (params[1] === "num") {
          return message.replace(":type", `Min value is ${params[0]}`);
        } else {
          return message.replace(":type", `Min length ${params[0]} chars`);
        }
      },
    },
    maxDecimal: {
      message: "Enter only Four Decimal values",
      rule: (val: any, params?: any, validator?: any): boolean => {
        const re = new RegExp(`^\\d+\\.?\\d{0,${params[0]}}$`);

        if (re.test(val)) {
          return true;
        } else {
          return false;
        }
      },
    },
  };
};

/**
 * Message Replacement
 * @returns
 */
export const messageReplace = () => {
  return {
    messages: {
      alpha_num: "Special chars are not allowed",
      alpha_num_dash: "Special chars except dash (-) are not allowed",
      numeric: "Only numbers are allowed",
      // required: "validator.mandatory",
    },
  };
};

/**
 * parse the error message from object
 * @param {object} errorMessageObj
 * @returns formatted error string
 */
export const parseStandardAPIErrorMessage = (errorMessageObj: any) => {
  let prepareMessage = "";
  const errorDetails = errorMessageObj?.response?.data?.details;
  const errorMessage = errorMessageObj?.response?.data?.message;
  if (errorDetails) {
    if (errorDetails.length > 1) {
      for (let i = 0; i < errorDetails.length; i++) {
        const element = errorDetails[i];
        prepareMessage = prepareMessage + element + "\n";
      }
    } else {
      prepareMessage = errorDetails.at(0);
    }
    return prepareMessage;
  }
  if (errorMessage) {
    return errorMessage;
  }
  if (!errorMessage || !errorDetails) {
    return `Something Went Wrong!!!`;
  }
};

export function validateGetAPIResponse(res: any) {
  if (res?.status === 200 && res.data) return true;
  throw new Error();
}

export function validatePostAPIResponse(res: any) {
  // Accept 200, 201, and 202 status codes (202 is common for async operations)
  if ((res?.status === 200 || res?.status === 201 || res?.status === 202) && res.data) return true;
  const error = new Error(`Invalid POST response: status ${res?.status}, has data: ${!!res?.data}`);
  console.error("validatePostAPIResponse failed:", { status: res?.status, data: res?.data });
  throw error;
}
export function validatePutAPIResponse(res: any) {
  if (res?.status === 200 && res.data) return true;
  throw new Error();
}

export function logWithColor(message: string, color: any) {
  console.log(`%c ${message}`, `color:${color};font-weight:bolder;font-size:16px`);
}

export function deepCopy(obj: any) {
  return JSON.parse(JSON.stringify(obj));
}

export async function loadRemoteEnv(remoteUrl: string, envName: string): Promise<any> {
  try {
    const res = await fetch(`${remoteUrl}/env.js`, { cache: "no-store" });
    const text = await res.text();

    // Create a sandbox and execute the JS safely in isolation
    const sandbox = { window: {} as any };
    new Function("window", text)(sandbox.window);

    // Extract the resulting env object
    const envObject = sandbox.window.env;

    // Validate it’s an object before assigning
    if (envObject && typeof envObject === "object") {
      (window as any)[envName] = envObject;
      console.log(`✅ ${envName} loaded`, envObject);
      return envObject;
    } else {
      throw new Error("env.js did not return a valid object");
    }
  } catch (err) {
    console.error(`❌ Failed to load env from ${remoteUrl}`, err);
    return {};
  }
}

export function constructProductionUrl(origin: string): string {
  const baseDomain = getEnvVariableValue(ENV_VAR.BASE_DOMAIN);

  if (!baseDomain) {
    console.warn("BASE_DOMAIN environment variable not found, using origin as fallback");
    return origin;
  }

  try {
    const url = new URL(origin);
    const hostname = url.hostname;

    /**
     * Extracts tenant name from subdomain by removing the last hyphen-separated segment.
     * The last segment is assumed to be a dynamic suffix (like remoteName, remote, etc.).
     * Tenant names can contain hyphens, so we preserve everything except the last segment.
     * Examples:
     * - tenant-remoteName -> tenant
     * - sec-audit-two-remoteName -> sec-audit-two
     * - tenant-anything -> tenant
     */
    const extractTenantName = (subdomain: string): string => {
      const parts = subdomain.split("-");
      // If there's only one part, return it as-is (no suffix to remove)
      if (parts.length <= 1) {
        return subdomain;
      }
      // Remove the last segment (dynamic suffix) and join the rest
      return parts.slice(0, -1).join("-");
    };

    // Check if the hostname already contains the base domain
    if (hostname.includes(baseDomain)) {
      // Extract tenant name and remove subdomain suffix
      // Example: viamagus-enterprisegpt.synthia.dev.vm.web-devapp.com -> viamagus.app.synthia.dev.vm.web-devapp.com
      // Example: sec-audit-two-remoteName.synthia.dev.vm.web-devapp.com -> sec-audit-two.app.synthia.dev.vm.web-devapp.com
      const tenantWithSubdomain = hostname.replace(`.${baseDomain}`, "");
      const cleanedTenant = extractTenantName(tenantWithSubdomain);
      const constructedHostname = `${cleanedTenant}.app.${baseDomain}`;
      return `${url.protocol}//${constructedHostname}${url.port ? `:${url.port}` : ""}`;
    } else {
      // If hostname doesn't contain base domain, treat the first part as tenant
      // Example: tenant-remoteName.example.com -> tenant.app.synthia.dev.vm.web-devapp.com
      // Example: sec-audit-two-remoteName.synthia.devapp -> sec-audit-two.app.synthia.dev.vm.web-devapp.com
      const hostnameParts = hostname.split(".");
      if (hostnameParts.length >= 2) {
        const tenant = extractTenantName(hostnameParts[0]);
        const constructedHostname = `${tenant}.app.${baseDomain}`;
        return `${url.protocol}//${constructedHostname}${url.port ? `:${url.port}` : ""}`;
      } else {
        // If no tenant, use app.BASE_DOMAIN directly
        const constructedHostname = `app.${baseDomain}`;
        return `${url.protocol}//${constructedHostname}${url.port ? `:${url.port}` : ""}`;
      }
    }
  } catch (error) {
    console.error("Error constructing production URL:", error);
    return origin;
  }
}
